import type { TRPCLink } from '@trpc/client';
import { observable } from '@trpc/server/observable';

import type { AppRouter } from './router.types';

export interface AuthLinkOptions {
  /** 액세스 토큰을 다시 받는다. 성공하면 보관소가 이미 갱신된 상태다. */
  refresh: () => Promise<unknown>;
  /** 재발급까지 실패해 세션이 끊겼을 때. 화면 상태를 비우는 것은 부르는 쪽 몫이다. */
  onSessionLost: () => void;
}

/**
 * 재발급 절차 자신. 여기서 UNAUTHORIZED 가 났다면 재발급이 실패한 것이므로,
 * 다시 재발급을 부르면 스스로를 부르는 고리가 된다.
 */
const REFRESH_PATH = 'auth.refresh';

/**
 * 액세스 토큰이 만료된 요청을 되살리는 링크.
 *
 * 만료는 15분마다 반드시 오는 정상 상태다 — 그때마다 사용자를 로그인 화면으로 보내는 대신
 * 토큰을 다시 받아 **원래 요청을 한 번만** 다시 보낸다.
 *
 * 한 번으로 못을 박는 이유: 재발급이 성공했는데도 또 UNAUTHORIZED 라면 토큰 문제가
 * 아니라는 뜻이고, 계속 시도하면 서버에 같은 요청을 무한히 던진다.
 *
 * HTTP 상태가 아니라 절차별 에러 코드를 보는 이유: 요청을 묶어 보내면(httpBatchLink)
 * 섞인 응답은 207 로 오고, 그 안의 401 은 상태 코드만 봐서는 보이지 않는다.
 */
export function createAuthLink({ refresh, onSessionLost }: AuthLinkOptions): TRPCLink<AppRouter> {
  return () =>
    ({ op, next }) =>
      observable((observer) => {
        let isRetried = false;
        let isCancelled = false;

        const attempt = () =>
          next(op).subscribe({
            next: (value) => observer.next(value),
            complete: () => observer.complete(),
            error: (error) => {
              const isRecoverable =
                !isRetried && op.path !== REFRESH_PATH && error.data?.code === 'UNAUTHORIZED';
              if (!isRecoverable) {
                observer.error(error);
                return;
              }

              isRetried = true;
              void refresh()
                .then(
                  () => {
                    if (!isCancelled) {
                      subscription = attempt();
                    }
                  },
                  () => {
                    // 원래 실패를 그대로 올린다 — 화면이 알아야 하는 것은 "재발급이 깨졌다"가
                    //   아니라 "이 요청이 인증되지 않았다"이고, 재발급 쪽 사정은 세션 정리로 끝난다.
                    onSessionLost();
                    if (!isCancelled) {
                      observer.error(error);
                    }
                  },
                )
                .catch(() => {
                  // 재시도를 거는 것 자체가 던진 경우다. 여기서 놓치면 아무도 답을 받지 못해
                  //   화면이 영원히 기다린다 — 원래 실패로 끝맺는다.
                  if (!isCancelled) {
                    observer.error(error);
                  }
                });
            },
          });

        let subscription = attempt();

        return () => {
          isCancelled = true;
          subscription.unsubscribe();
        };
      });
}
